/**
 * Singleton holding the live DOM anchor for the font toolbar.
 *
 * `EditorView` renders an empty `<div>` inside its workspace panel and
 * passes `setFontToolbarHost` as the React `ref` callback. The desktop
 * `FontToolbar` portals itself into that element, and the editor's
 * click-outside / `selection:cleared` guards use it to recognise clicks
 * that originated inside the toolbar.
 */
let host: HTMLElement | null = null;

/**
 * React `ref` callback that should be attached to the host `<div>` inside
 * `EditorView`. React calls it with the element on mount and with `null`
 * on unmount, so the value here always matches the live DOM.
 */
export const setFontToolbarHost = (element: HTMLElement | null): void => {
  host = element;
};

/** Returns the currently mounted host element, or `null` if none is mounted. */
export const getFontToolbarHost = (): HTMLElement | null => host;

/**
 * Convenience helper used by click-outside / `selection:cleared` guards
 * to recognise interactions that happened inside the toolbar (and should
 * therefore not deselect the active canvas object).
 */
export const isInsideFontToolbar = (target: EventTarget | Element | null): boolean => {
  if (!host || !target || !(target instanceof Element)) {
    return false;
  }
  return host.contains(target);
};
